// Phase 83z.12 — Hero Tournament Banner carousel
// • Removed floating pickleball + particle background (user spec: too busy).
// • Restructured layout: subtitle / title (wider, no 60% cap) / date / location
//   / countdown chip / prize-pool badge / 2 CTAs (Xem chi tiết + Tham gia).
// • "Tham gia" → đăng ký page. Flips to "Đã đăng ký" (disabled) when user
//   already has any registration for this tournament.
// • Prize-pool badge auto-hides when total = 0 (BTC chưa nhập).
import { asset } from "../../lib/asset";
import { useEffect, useState } from "react";
import { m as motion, AnimatePresence } from "framer-motion";
import { CalendarBlank, MapPin, CaretRight, Trophy, Check } from "@phosphor-icons/react";
import { daysLeftLabel } from "../../lib/daysLeftLabel";

function _formatVnd(n) {
  const v = Math.max(0, Math.floor(Number(n) || 0));
  return `${v.toLocaleString("vi-VN")}₫`;
}

export default function HeroBanner({ tournaments = [], onView, onJoin }) {
  const [idx, setIdx] = useState(0);
  const list = tournaments.length ? tournaments : [];

  useEffect(() => {
    if (list.length <= 1) return;
    const t = setInterval(() => setIdx((i) => (i + 1) % list.length), 5000);
    return () => clearInterval(t);
  }, [list.length]);

  if (list.length === 0) {
    return (
      <div
        className="mx-5 mt-4 h-[280px] rounded-[28px] border border-dashed border-white/10 flex items-center justify-center text-[#94A3B8] text-sm"
        data-testid="home-hero-empty"
      >
        Chưa có giải đấu nổi bật.
      </div>
    );
  }

  const item = list[idx];
  const prize = Number(item.totalPrizePool || 0);
  const regClosed = item.registrationStatus === "closed";
  const isRegistered = Boolean(item.isRegistered);
  const isLive = item.status === "live"; // Phase 84.M.28

  return (
    <div className="mx-5 mt-4" data-testid="home-hero-banner">
      <div
        className="relative overflow-hidden rounded-[28px] sm:rounded-[32px] min-h-[300px]"
        style={{
          background:
            "linear-gradient(135deg, #0B1220 0%, #060B14 50%, #111827 100%)",
        }}
      >
        {/* Tournament background image — HAR-19: WebP responsive (7-9KB vs 1.5MB PNG). */}
        <picture>
          <source
            srcSet={`${asset("/home-assets/hero-bg-tournament-640.webp")} 640w, ${asset("/home-assets/hero-bg-tournament.webp")} 1280w`}
            sizes="(max-width: 640px) 640px, 1280px"
            type="image/webp"
          />
          <img
            src={asset("/home-assets/hero-bg-tournament.webp")}
            alt=""
            loading="lazy"
            decoding="async"
            aria-hidden="true"
            className="absolute inset-0 w-full h-full object-cover opacity-30 mix-blend-screen pointer-events-none"
          />
        </picture>
        {/* Lime radial glow */}
        <div
          className="absolute inset-0"
          style={{
            background:
              "radial-gradient(circle at 80% 25%, rgba(168,255,0,0.16) 0%, transparent 55%)",
          }}
        />

        {/* Content */}
        <AnimatePresence mode="wait">
          <motion.div
            key={item.id}
            initial={{ opacity: 0, x: -16 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: 16 }}
            transition={{ duration: 0.6, ease: [0.32, 0.72, 0, 1] }}
            className="relative h-full flex flex-col gap-3 p-5 sm:p-6 z-10"
          >
            {/* Subtitle row + Live badge — Phase 84.M.28 */}
            <div className="flex items-center gap-2 flex-wrap">
              <div className="text-[10px] sm:text-[11px] font-bold uppercase tracking-[0.18em] text-[#A8FF00]">
                {item.subtitle || "PICKLEBALL"}
              </div>
              {isLive && (
                <motion.span
                  className="inline-flex items-center gap-1.5 px-2 py-[3px] rounded-full text-[9.5px] font-black uppercase tracking-[0.16em] text-white border"
                  style={{
                    background: "linear-gradient(135deg, #FF3B30 0%, #C72020 100%)",
                    borderColor: "rgba(255, 80, 80, 0.65)",
                    boxShadow: "0 0 14px rgba(255, 59, 48, 0.55)",
                  }}
                  animate={{ opacity: [1, 0.55, 1] }}
                  transition={{ duration: 1.4, repeat: Infinity, ease: "easeInOut" }}
                  data-testid="home-hero-live-badge"
                >
                  <span className="w-1.5 h-1.5 rounded-full bg-white shadow-[0_0_6px_rgba(255,255,255,0.9)]" />
                  Đang diễn ra
                </motion.span>
              )}
            </div>
            {/* Title — full width now (no 60% cap) */}
            <div className="text-[24px] sm:text-[28px] font-black text-white leading-[1.05] tracking-tight pr-2">
              {item.title}
            </div>
            {/* Meta row */}
            <div className="flex flex-wrap items-center gap-x-4 gap-y-1.5 text-[#94A3B8] text-[12.5px] font-medium">
              {item.date && (
                <span className="inline-flex items-center gap-1.5">
                  <CalendarBlank size={14} weight="bold" />
                  {item.date}
                </span>
              )}
              {item.location && (
                <span className="inline-flex items-center gap-1.5">
                  <MapPin size={14} weight="bold" />
                  {item.location}
                </span>
              )}
            </div>
            {/* Countdown + Prize-pool badge row */}
            <div className="flex flex-wrap items-center gap-2">
              {!regClosed && daysLeftLabel(item.daysLeft) && (
                <span
                  className="inline-flex items-center px-3 py-1 rounded-full text-[11px] font-bold border border-[#A8FF00]/40 text-[#A8FF00] bg-[#A8FF00]/5"
                  data-testid="home-hero-countdown"
                >
                  {daysLeftLabel(item.daysLeft)}
                </span>
              )}
              {regClosed && (
                <span
                  className="inline-flex items-center px-3 py-1 rounded-full text-[11px] font-bold border border-white/15 text-white/55 bg-white/[0.04]"
                  data-testid="home-hero-reg-closed"
                >
                  Đã đóng đăng ký
                </span>
              )}
              {/* Phase 83z.12 — Premium gold prize-pool badge. Hidden when 0. */}
              {prize > 0 && (
                <motion.span
                  initial={{ scale: 0.85, opacity: 0 }}
                  animate={{ scale: 1, opacity: 1 }}
                  transition={{ type: "spring", stiffness: 220, damping: 14, delay: 0.15 }}
                  className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-[11px] font-black uppercase tracking-wider text-[#0B0700]"
                  style={{
                    background: "linear-gradient(135deg, #FFE17B 0%, #F5C518 45%, #FF9F1C 100%)",
                    boxShadow: "0 0 18px rgba(245, 197, 24, 0.45), inset 0 1px 0 rgba(255,255,255,0.55)",
                  }}
                  data-testid="home-hero-prize-badge"
                  title="Tổng giải thưởng"
                >
                  <Trophy size={12} weight="fill" />
                  {_formatVnd(prize)}
                </motion.span>
              )}
            </div>
            {/* CTA row */}
            <div className="flex items-center gap-2 mt-1">
              <button
                type="button"
                onClick={() => onView?.(item)}
                className="inline-flex items-center gap-1.5 px-4 py-2.5 rounded-[16px] text-white font-bold text-[13px] border border-white/15 hover:bg-white/5 transition active:scale-95"
                data-testid="home-hero-detail-btn"
              >
                Xem chi tiết
                <CaretRight size={12} weight="bold" />
              </button>
              {!regClosed && (
                isRegistered ? (
                  <button
                    type="button"
                    onClick={() => onView?.(item)}
                    className="inline-flex items-center gap-1.5 px-4 py-2.5 rounded-[16px] text-white font-bold text-[13px] bg-gradient-to-r from-[#10B981] to-[#059669] shadow-[0_0_18px_rgba(16,185,129,0.4)] active:scale-95 transition"
                    data-testid="home-hero-registered-btn"
                    title="Bấm để xem đăng ký của bạn"
                  >
                    <Check size={13} weight="bold" />
                    Đã đăng ký
                  </button>
                ) : (
                  <button
                    type="button"
                    onClick={() => (onJoin || onView)?.(item)}
                    className="inline-flex items-center gap-1.5 px-4 py-2.5 rounded-[16px] text-white font-bold text-[13px] bg-gradient-to-r from-[#FF8A00] to-[#FF9F1C] shadow-[0_0_20px_rgba(255,138,0,0.45)] active:scale-95 transition"
                    data-testid="home-hero-join-btn"
                  >
                    Tham gia
                    <CaretRight size={12} weight="bold" />
                  </button>
                )
              )}
            </div>
          </motion.div>
        </AnimatePresence>
      </div>
      {list.length > 1 && (
        <div className="flex items-center justify-center gap-1.5 mt-3" data-testid="home-hero-dots">
          {list.map((_, i) => (
            <button
              key={i}
              onClick={() => setIdx(i)}
              className={`h-1.5 rounded-full transition-all ${
                i === idx ? "w-6 bg-[#FF8A00]" : "w-1.5 bg-white/15"
              }`}
              aria-label={`Slide ${i + 1}`}
            />
          ))}
        </div>
      )}
    </div>
  );
}
